import React, {
    Fragment,
    Suspense,
    useCallback,
    useEffect,
    useMemo,
    useRef
} from "react";

import { css } from "@compiled/react";

import type { ExtensionHandlers } from "@atlaskit/editor-common/extensions";
import { type RendererProps } from "@atlaskit/renderer";
import { token } from "@atlaskit/tokens";

import { Button, Link } from "@/libraries/design/components";
import { LogoV3 } from "@/libraries/wpl/foundations/src";

import { Source } from "../ai-summary";
import { useAISummaryContext } from "../ai-summary-context";

import { encodeAdf } from "./encoder";

const AkReactRenderer = React.lazy(() =>
    import("@atlaskit/renderer").then(({ ReactRenderer }) => ({
        default: ReactRenderer
    }))
);

export type MarkdownRendererProps = {
    answerString: string;
    statusComponent: (props: {
        text?: string;
        sources?: Source[];
    }) => React.ReactNode;
    query?: string;
    isSourceFiltered?: boolean;
};

const MediaComponent = () => <Fragment />;
const MediaSingleComponent = () => <Fragment />;

const buttonWrapperStyle = css({
    display: "inline-block",
    marginBlockStart: token("space.100"),
    marginInlineEnd: token("space.100")
});

const rovoButtonWrapperStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    marginBlockStart: token("space.200")
});

const rovoButtonStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    maxWidth: "none",
    height: "20px"
});

const HIGHLIGHT_NAME = "wac-citation-highlight";

/**
 * Checks whether the CSS Custom Highlight API is available in the browser.
 */
function supportsHighlightAPI(): boolean {
    return (
        typeof globalThis.CSS !== "undefined" && "highlights" in globalThis.CSS
    );
}

/**
 * Collects Range objects covering content nodes that immediately precede
 * a citation marker element, stopping at the previous marker or the start
 * of the parent. Trims leading sentence-ending punctuation (e.g. ". ")
 * from the earliest node so the previous sentence's period isn't highlighted.
 */
function collectPrecedingRanges(marker: Element): Range[] {
    const parent = marker.parentNode;
    if (!parent) return [];

    const ranges: Range[] = [];
    let sibling: Node | null = marker.previousSibling;

    while (sibling) {
        if (
            sibling instanceof HTMLElement &&
            sibling.hasAttribute("data-citation-marker")
        ) {
            break;
        }

        if ((sibling.textContent || "").trim().length > 0) {
            const range = new Range();
            range.selectNodeContents(sibling);
            ranges.push(range);
        }

        sibling = sibling.previousSibling;
    }

    // The last range in the array is the earliest (furthest-back) node.
    // Trim any leading sentence-ending punctuation so e.g. ". Some text"
    // starts the highlight at "Some" instead of at the period.
    if (ranges.length > 0) {
        const earliest = ranges[ranges.length - 1];
        const node = earliest?.startContainer;

        if (node?.nodeType === Node.TEXT_NODE) {
            const text = node.textContent || "";
            const match = /^[.!?;:,\s]+/.exec(text);
            if (match) {
                earliest?.setStart(node, match[0].length);
            }
        }
    }

    return ranges;
}

const getRovoChatUrl = (query: string) => {
    const chatUrl = new URL("https://home.atlassian.com/chat");
    chatUrl.searchParams.set("rovoChatPathway", "chat");
    chatUrl.searchParams.set("rovoChatPrompt", query);

    const loginUrl = `https://id.atlassian.com/login?continue=${encodeURIComponent(
        chatUrl.toString()
    )}`;

    return loginUrl;
};

const RovoButton = ({ text, href }: { text: string; href: string }) => (
    <Button
        variant="secondary"
        colorAgainst="white"
        css={rovoButtonStyle}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
    >
        <LogoV3
            brand="rovo"
            type="mark"
            appearance="brand"
            width={20}
            height={20}
        />
        <span>{text}</span>
    </Button>
);

const ButtonComponent = ({
    text,
    buttonType,
    href
}: {
    text: string;
    buttonType: string;
    href: string;
}) => {
    if (buttonType === "navigation") {
        return (
            <Link
                href={href}
                type="primary"
                aria-label={`${text}button`}
                text={text}
            />
        );
    }

    if (buttonType === "rovo") {
        return <RovoButton text={text} href={href} />;
    }

    return (
        <Button
            variant="primary_alt"
            size="small"
            colorAgainst="white"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
        >
            {text}
        </Button>
    );
};

const createCtaButtonExtensionHandler = (
    isRovoEnabledCohort: boolean,
    query?: string
): ExtensionHandlers => ({
    "com.atlassian.wac": (ext) => {
        const { extensionKey, parameters } = ext;

        if (extensionKey === "cta-button" && parameters) {
            const { text, buttonType, href } = parameters as {
                text: string;
                buttonType: string;
                href: string;
            };

            const isRovoButton = buttonType === "rovo";
            const resolvedText =
                isRovoButton && isRovoEnabledCohort
                    ? "Send this to Rovo"
                    : text;
            const resolvedHref =
                isRovoButton && isRovoEnabledCohort
                    ? getRovoChatUrl(query || "")
                    : href;

            // Wrap in span to ensure inline rendering
            return (
                <div
                    css={[
                        buttonWrapperStyle,
                        isRovoButton && rovoButtonWrapperStyle
                    ]}
                >
                    <ButtonComponent
                        text={resolvedText}
                        buttonType={buttonType}
                        href={resolvedHref}
                    />
                </div>
            );
        }

        return null;
    }
});

// Extension handler for CTA buttons
// Handles inlineExtension nodes with extensionKey: "cta-button"
export const MarkdownRenderer = ({
    answerString,
    statusComponent,
    query,
    isSourceFiltered
}: MarkdownRendererProps) => {
    const { isRovoEnabledCohort } = useAISummaryContext();
    const containerRef = useRef<HTMLDivElement>(null);

    const docContent = useMemo(
        () => encodeAdf(answerString, false),
        [answerString]
    );

    const ctaButtonExtensionHandler = useMemo(
        () =>
            createCtaButtonExtensionHandler(
                Boolean(isRovoEnabledCohort),
                query
            ),
        [isRovoEnabledCohort, query]
    );

    // Wrap status component in a marker span so we can locate it in the DOM
    const wrappedStatusComponent = useCallback(
        (props: { text?: string; sources?: Source[] }) => (
            <span data-citation-marker="">{statusComponent(props)}</span>
        ),
        [statusComponent]
    );

    // Inject a ::highlight() style rule for the CSS Custom Highlight API
    useEffect(() => {
        if (!supportsHighlightAPI()) return;

        const style = document.createElement("style");
        style.textContent = `::highlight(${HIGHLIGHT_NAME}) { background-color: ${token(
            "color.background.accent.blue.subtlest"
        )}; }`;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Clear the highlight when the source filter is dismissed
    useEffect(() => {
        if (!isSourceFiltered && supportsHighlightAPI()) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ((CSS as any).highlights as Map<string, unknown>).delete(
                HIGHLIGHT_NAME
            );
        }
    }, [isSourceFiltered]);

    // Event delegation: clicking a citation highlights its preceding text
    // via the CSS Highlight API (no DOM mutation, so no React conflicts)
    useEffect(() => {
        const container = containerRef.current;
        if (!container || !supportsHighlightAPI()) return;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const highlights = (CSS as any).highlights as Map<string, unknown>;

        const handler = (e: Event) => {
            const target = e.target as HTMLElement;
            const marker = target.closest("[data-citation-marker]");

            if (!marker) return;

            // Clear previous highlight before applying new one
            highlights.delete(HIGHLIGHT_NAME);

            const ranges = collectPrecedingRanges(marker);

            if (ranges.length > 0) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
                const highlight = new ((globalThis as any).Highlight as new (
                    ...args: Range[]
                ) => unknown)(...ranges);
                highlights.set(HIGHLIGHT_NAME, highlight);
            }
        };

        container.addEventListener("click", handler);
        return () => container.removeEventListener("click", handler);
    }, []);

    const config: RendererProps = {
        // @ts-expect-error - DocNode type unavailable, fallback to unknown
        document: docContent as unknown,
        nodeComponents: {
            status: wrappedStatusComponent,
            media: MediaComponent,
            mediaSingle: MediaSingleComponent
        },
        extensionHandlers: ctaButtonExtensionHandler,
        smartLinks: {
            ssr: true
        }
    };

    return (
        <div ref={containerRef}>
            <Suspense fallback={<Fragment />}>
                <AkReactRenderer {...config} />
            </Suspense>
        </div>
    );
};
